import {
  inject,
  InjectionToken,
  Injector,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { isPlatformServer, Location } from '@angular/common';
import { Router, Routes } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import type { PageRegistryDto } from '@api/models';
import { parseUrlPath } from '../helpers/parse-url-path.function';
import { PageRegistryService } from '../services/page-registry.service';
import { RU_ROUTE_PREFIX, UA_ROUTE_PREFIX } from '../constants';

export const PAGE_TOKEN = new InjectionToken<PageRegistryDto>('page_token');
export const PAGE_STATE_KEY = makeStateKey<PageRegistryDto>('page_state_key');

export function isPathInRoutes(path: string, routes: Routes): boolean {
  const { urlObject } = parseUrlPath(path);
  let pathname = urlObject.pathname || '';
  if (pathname.startsWith('/')) { pathname = pathname.slice(1); }
  const segments = pathname.split('/');
  if (segments[0] !== UA_ROUTE_PREFIX) {
    segments.unshift(RU_ROUTE_PREFIX);
  }

  let currentRoutes = routes;
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const route = currentRoutes.find(r => r.path === segment);

    const isLastSegment = i === segments.length - 1;
    if (!route) {
      return false;
    }

    if (isLastSegment) {
      return true;
    } else {
      currentRoutes = route.children || [];
    }
  }
  return false;
}

export function initRouteResolver() {
  return (): Observable<unknown> => {
    const injector = inject(Injector);
    const state = inject(TransferState);
    const platformId = inject(PLATFORM_ID);
    const location = inject(Location);
    const pageRegistryService = inject(PageRegistryService);
    const router = inject(Router);

    if (isPathInRoutes(location.path(), router.config)) {
      return;
    }

    let currentPage: PageRegistryDto | null;

    if (isPlatformServer(platformId)) {
      currentPage = injector.get(PAGE_TOKEN, null); // token is present only in Server & if dynamic page is requested
      state.set(PAGE_STATE_KEY, currentPage);
    } else {
      currentPage = state.get(PAGE_STATE_KEY, null);
    }

    if (currentPage) {
      pageRegistryService.registerPages([currentPage]);
    } else {
      const { slug } = parseUrlPath(location.path());

      return pageRegistryService.fetchAndRegisterPage$(slug).pipe(catchError(() => of(null)));
    }
  };
}
